import { hasPerm, type Me } from '@/shared/api/session'
import type { Capabilities } from '@/shared/api/processConfig'
import {
  ADMIN_NAV, DEFAULT_ADMIN_DEST,
  type AdminDest, type AdminSectionDef,
} from '@/shared/routes'

type SubDef = AdminSectionDef['subs'][number]

function canSee(me: Me | undefined, caps: Capabilities | undefined, sub: SubDef): boolean {
  if (sub.needs && !caps?.[sub.needs]) return false
  const { gate } = sub
  return 'adminOnly' in gate ? !!me?.isAdmin : hasPerm(me, gate.perm)
}

/** The admin nav with everything this account may not open removed: sub-tabs
 *  they lack the permission for or whose capability the API has not
 *  reported, then sections left with nothing in them. */
export function visibleAdminNav(me: Me | undefined, caps?: Capabilities): AdminSectionDef[] {
  return ADMIN_NAV
    .map(s => ({ ...s, subs: s.subs.filter(sub => canSee(me, caps, sub)) }))
    .filter(s => s.subs.length > 0)
}

/** The destination to actually render for a requested one. A section or
 *  sub-tab this account cannot open falls back to their first available one
 *  rather than rendering nothing. */
export function resolveAdminDest(nav: AdminSectionDef[], requested: AdminDest): AdminDest {
  const section = nav.find(s => s.key === requested.section) ?? nav[0]
  if (!section) return DEFAULT_ADMIN_DEST
  const sub = section.subs.find(s => s.key === requested.sub) ?? section.subs[0]
  return { section: section.key, sub: sub.key }
}
